import { useQuery } from '@tanstack/react-query';

import {
  COMPLETED_ONBOARDING_STATUS,
  getPersistedOnboardingComplete,
} from '@/lib/localStorage/persistedOnboardingComplete';
import type { OnboardingStatus } from '@/lib/onboarding/service';
import { onboardingService } from '@/lib/onboarding/service';

/**
 * Backoff 2s -> 30s instead of polling at 2s forever.
 *
 * Onboarding completion is user-driven and an org can sit incomplete
 * indefinitely, so the flat 2s interval was a permanent heartbeat: every
 * response re-rendered each consumer, and this hook is read by
 * ProposalCenter — so it re-rendered the whole AI Actions detail pane
 * every 2 seconds while the user was typing in the email editor.
 *
 * The first ~10 polls stay at 2s, which covers the window where someone
 * is actively working through onboarding; after that it decays so an
 * abandoned/parked org costs a request every 30s rather than 1800/hour.
 */
export function computeOnboardingPollInterval(
  status: OnboardingStatus | undefined,
  pollCount: number
): number | false {
  if (!status || status.pipeline_complete) {
    return false;
  }
  const backoffStep = Math.floor(pollCount / 10);
  return Math.min(2_000 * 2 ** backoffStep, 30_000);
}

export function useOnboardingStatusQuery(orgId: number | undefined) {
  const isPersistedComplete = !!getPersistedOnboardingComplete(orgId);
  const query = useQuery({
    queryKey: ['onboarding-status', orgId],
    queryFn: () => onboardingService.getStatus(orgId ?? 0),
    enabled: !!orgId && !isPersistedComplete,
    initialData: isPersistedComplete ? COMPLETED_ONBOARDING_STATUS : undefined,
    // () => 0 marks persisted data as epoch-old so staleTime controls freshness;
    // undefined when not persisted lets React Query refetch normally on mount.
    initialDataUpdatedAt: isPersistedComplete ? () => 0 : undefined,
    staleTime: 2_000,
    refetchInterval: q =>
      computeOnboardingPollInterval(q.state.data, q.state.dataUpdateCount),
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
  return { query, isPersistedComplete };
}
